import React from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DestinoPagoOpcion } from '@/presupuestos/types';

export const TEXTO_SIN_DESTINO = 'Falta indicar el destino de pago';

interface SelectorDestinoPagoProps {
  value: string | null | undefined;
  onChange: (destinoPagoId: string) => void;
  opciones: DestinoPagoOpcion[];
  ariaLabel: string;
  disabled?: boolean;
  /** Versión chica para la fila de un ítem. */
  compacto?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Selector de la cuenta a la que va el dinero de un ítem. Solo ofrece los
 * destinos activos, más el actual del ítem aunque esté desactivado. Sin
 * destino, se resalta de forma sutil.
 */
export const SelectorDestinoPago: React.FC<SelectorDestinoPagoProps> = ({
  value,
  onChange,
  opciones,
  ariaLabel,
  disabled = false,
  compacto = false,
  onOpenChange,
}) => {
  const disponibles = opciones.filter((opcion) => opcion.activo || opcion.id === value);
  const sinDestino = !value;

  return (
    <span className="inline-flex items-center gap-1" title={sinDestino ? TEXTO_SIN_DESTINO : undefined}>
      {sinDestino && (
        <AlertCircle aria-label={TEXTO_SIN_DESTINO} className="h-3.5 w-3.5 shrink-0 text-amber-600" />
      )}
      <Select value={value ?? ''} onValueChange={onChange} disabled={disabled} onOpenChange={onOpenChange}>
        <SelectTrigger
          size="sm"
          aria-label={ariaLabel}
          className={
            compacto
              ? `h-6 px-2 py-0 text-xs shadow-none ${sinDestino ? 'border-amber-400' : 'border-0'}`
              : sinDestino
                ? 'border-amber-400'
                : undefined
          }
        >
          <SelectValue placeholder="Sin destino" />
        </SelectTrigger>
        <SelectContent>
          {disponibles.map((opcion) => (
            <SelectItem key={opcion.id} value={opcion.id}>
              {opcion.nombre}
              {!opcion.activo && ' (inactivo)'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  );
};
